import React, { useCallback, useEffect } from 'react';
import { type SubmitHandler, useFormContext } from 'react-hook-form';
import * as constants from '@lindar-joy/plugin-default-event-tracking-advanced-browser/lib/cjs/constants';
import Auth from 'modules/Auth';
import amplitude from 'lib/analytics';
import { FormError, VerificationCodeInput } from 'components/Form';
import { replaceItemsInString } from 'lib/formatters/replaceItemsInString';
import { useAppDispatch } from 'hooks/redux';
import { useMultiFactorLogin } from '../../hooks';
import { type MultiFactorAuthFormFields } from '../../types';
import { CODE_LENGTH, FORM_NAME } from '../../constants';
import MultiFactorAuthWrapper from './components/MultiFactorAuthWrapper';
import { multiFactorAuthData } from './data';

const { MULTI_FACTOR_FAILURE_TYPE, MULTI_FACTOR_METHODS } = Auth.constants;

export const MultiFactorAuthForm = () => {
  const dispatch = useAppDispatch();

  const {
    handleSubmit,
    setError,
    resetField,
    formState: { isValid, isSubmitting }
  } = useFormContext<MultiFactorAuthFormFields>();

  const {
    makeLoginRequest,
    isLoading: isLoginLoading,
    error: loginError,
    multiFactorAuthentication
  } = useMultiFactorLogin();

  const { mfaCodeDeliveredTo, mfaFailureType, redactedPhoneNumber, email } =
    multiFactorAuthentication;

  const backToLogin = () => {
    dispatch(Auth.actions.clearMultiFactorAuthentication());
  };

  const onSubmit: SubmitHandler<MultiFactorAuthFormFields> = useCallback(
    async (data) => {
      const params = new FormData();

      params.append('mfaCode', data.multiFactorAuthCode);

      try {
        await makeLoginRequest(params);
      } catch (err: unknown) {
        const typedError = err as {
          success: boolean;
          message: string;
          data?: { mfaFailureType: string };
        };
        if (typedError?.data?.mfaFailureType === MULTI_FACTOR_FAILURE_TYPE.INVALID_MFA_CODE) {
          setError('multiFactorAuthCode', {
            type: 'manual',
            message: typedError.message
          });
          resetField('multiFactorAuthCode', {
            defaultValue: '',
            keepError: true,
            keepDirty: true,
            keepTouched: true
          });
        } else if (!typedError.success && !typedError?.data?.mfaFailureType) {
          setError('root.serverError', {
            type: 'manual',
            message: typedError.message
          });
        }

        // Delete once tracking is implemented with react hook form
        amplitude.track('Form Submission Rejected', {
          [constants.AMPLITUDE_EVENT_PROP_FORM_NAME]: FORM_NAME,
          'Error Message': typedError.message
        });
      }
    },
    [makeLoginRequest, setError, resetField]
  );

  const formHandleSubmit = useCallback(() => {
    void handleSubmit(onSubmit);
  }, [handleSubmit, onSubmit]);

  useEffect(() => {
    if (isValid) {
      void handleSubmit(onSubmit)();
    }
  }, [formHandleSubmit, handleSubmit, isValid, onSubmit]);

  if (mfaFailureType === MULTI_FACTOR_FAILURE_TYPE.TOO_MANY_FAILED_MFA_CODE_CHECKS) {
    return (
      <MultiFactorAuthWrapper
        title={multiFactorAuthData.tooManyAttempts.title}
        body={loginError ?? multiFactorAuthData.tooManyAttempts.body}
        buttonText={multiFactorAuthData.tooManyAttempts.buttonText}
        onSubmit={backToLogin}
      />
    );
  }

  return (
    <MultiFactorAuthWrapper
      title={multiFactorAuthData.newDevice.title}
      body={replaceItemsInString(multiFactorAuthData.newDevice.body, {
        methodType: mfaCodeDeliveredTo.toLocaleLowerCase(),
        method:
          (mfaCodeDeliveredTo === MULTI_FACTOR_METHODS.SMS ? redactedPhoneNumber : email) ?? ''
      })}
      isSubmitDisabled={!isValid}
      isLoading={isLoginLoading || isSubmitting}
      buttonText={multiFactorAuthData.newDevice.buttonText}
      onSubmit={formHandleSubmit}
    >
      <VerificationCodeInput
        name="multiFactorAuthCode"
        label={multiFactorAuthData.newDevice.inputLabel}
        rules={{
          required: true,
          pattern: {
            value: new RegExp(`^\\d{${CODE_LENGTH}}$`),
            message: `Code must be exactly ${CODE_LENGTH} digits`
          }
        }}
      />
      <FormError name="root.serverError" />
    </MultiFactorAuthWrapper>
  );
};
